import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Logo } from '@/components/Logo'
import { supabase } from '@/lib/supabase'
import { useSession } from '@/lib/session'

/**
 * Landing page for the invite / reset link. Supabase establishes a session from
 * the link automatically; the athlete just sets their password here.
 */
export default function SetPassword() {
  const navigate = useNavigate()
  const { session, loading } = useSession()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Give supabase a moment to parse the token from the URL.
  const [settling, setSettling] = useState(true)

  useEffect(() => {
    const t = setTimeout(() => setSettling(false), 1200)
    return () => clearTimeout(t)
  }, [])

  const submit = async () => {
    if (password.length < 6) return setError('Password must be at least 6 characters.')
    if (password !== confirm) return setError('Passwords don’t match.')
    setBusy(true)
    setError(null)
    const { error } = await supabase.auth.updateUser({ password })
    setBusy(false)
    if (error) return setError(error.message)
    navigate('/')
  }

  const noSession = !loading && !settling && !session

  return (
    <div className="hero-glow relative flex min-h-full flex-col items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm animate-fade-in text-center">
        <Logo className="mx-auto h-14" />
        <p className="mt-4 font-condensed text-xs uppercase tracking-[0.35em] text-muted">Set your password</p>

        {noSession ? (
          <div className="mt-8">
            <p className="text-sm text-muted">
              This link is invalid or has expired. Ask your coach to resend your invite.
            </p>
            <Button variant="secondary" className="mt-4" onClick={() => navigate('/login')}>Go to sign in</Button>
          </div>
        ) : (
          <div className="mt-8 text-left">
            <label className="mb-1.5 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">New password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
            <label className="mb-1.5 mt-4 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">Confirm password</label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
            {error && <p className="mt-3 text-sm text-danger">{error}</p>}
            <Button block size="lg" className="mt-6" disabled={busy || !password} onClick={submit}>
              {busy ? 'Saving…' : 'Set password & continue'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
